import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { setSelectedMspId } from "../slices/selectedMspSlice";
import DeleteConfirmationModal from "../../../components/DeleteModal";
import Search from "../../../components/Search";
import { fetchMsps, MspRecord, deleteMsp } from "../services/mspService";
import Delete from "../../../assets/images/delete.png";
import CustomerLink from "../../../assets/images/customer-link.png";
import AddMspForm from "../AddMspForm";
import TableSkeleton from "../../../components/Skeleton/TableSkeleton";
import { useDebounce } from "use-debounce";
import { Empty } from "antd";
import { toast } from "react-toastify";
import { getVisiblePages } from "../../../utils/pagination";
import { formatDate } from "../../../utils/formatDate";
interface MspTableProps {
  onMspSelect?: (id: number) => void;
}

export default function MspTable({ onMspSelect }: Readonly<MspTableProps>) {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [showAddForm, setShowAddForm] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [rowToDelete, setRowToDelete] = useState<MspRecord | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [msps, setMsps] = useState<MspRecord[]>([]);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalRows: 0,
  });
  const [debouncedSearchTerm] = useDebounce(searchTerm, 300);
  const [deleting, setDeleting] = useState(false);
  const [refresh, setRefresh] = useState(0);

  useEffect(() => {
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  }, [debouncedSearchTerm]);

  useEffect(() => {
    const state = location.state;
    if (state?.resetMspView) {
      setShowAddForm(false);
      setSearchTerm("");
      setPagination((prev) => ({
        ...prev,
        currentPage: 1,
      }));
    }
  }, [location.state]);

  const handleRefresh = () => {
    setRefresh((prev) => prev + 1);
  };

  useEffect(() => {
    const loadMsps = async () => {
      setLoading(true);
      try {
        const res = await fetchMsps({
          page: pagination.currentPage,
          limit: 10,
          search: debouncedSearchTerm,
        });

        if (res && Array.isArray(res.data) && res.pagination) {
          setMsps(res.data);
          setPagination(res.pagination);
        } else {
          setMsps([]);
          setPagination({
            currentPage: 1,
            totalPages: 1,
            totalRows: 0,
          });
        }
      } catch (error: any) {
        setMsps([]);
        setPagination({
          currentPage: 1,
          totalPages: 1,
          totalRows: 0,
        });
        console.error("Failed to fetch MSPs:", error);
        toast.error("Failed to fetch MSPs");
      } finally {
        setLoading(false);
      }
    };

    loadMsps();
  }, [pagination.currentPage, debouncedSearchTerm, refresh]);

  const handleMspClick = (mspId: number) => {
    dispatch(setSelectedMspId(mspId));
    onMspSelect?.(mspId);
    navigate("/msp/" + mspId);
  };

  const handleViewCustomers = (mspId: number) => {
    navigate(`/msps/${mspId}/customers`);
  };

  const handleDeleteClick = (row: MspRecord) => {
    setRowToDelete(row);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!rowToDelete) return;
    try {
      setDeleting(true);
      const idNum = Number(rowToDelete.id);
      if (!Number.isFinite(idNum) || idNum <= 0) {
        toast.error("Invalid MSP id");
        return;
      }
      const resp = await deleteMsp(idNum);

      if (resp.status === "success") {
        setShowDeleteModal(false);
        setRowToDelete(null);
        handleRefresh();
        return;
      } else {
        const msg = resp.message || "MSP not found or already deleted";
        toast.info(msg, {
          position: "top-center",
          autoClose: 2000,
          pauseOnHover: false,
        });
      }

      setShowDeleteModal(false);
      setRowToDelete(null);
    } catch (error: any) {
      const msg =
        error?.message ||
        error?.response?.data?.message ||
        "Delete failed. Please try again.";
      toast.error(`Delete failed: ${msg}`, { position: "top-center" });
    } finally {
      setDeleting(false);
    }
  };

  if (showAddForm)
    return (
      <AddMspForm
        onCancel={() => setShowAddForm(false)}
        onSuccess={handleRefresh}
      />
    );

  return (
    <div className="px-2 pb-8">
      {showDeleteModal && (
        <DeleteConfirmationModal
          onConfirm={handleConfirmDelete}
          onCancel={() => {
            setShowDeleteModal(false);
            setRowToDelete(null);
          }}
          loading={deleting}
          entityType="msp"
          entityName={rowToDelete?.msp_name}
        />
      )}

      <div className="pt-2 flex items-start justify-between">
        <div>
          <h2 className="text-[22px] font-semibold text-[#4F4F4F]">
            Managed Service Provider List
          </h2>
          <p className="text-[#4F4F4F] text-[14px] mt-1">
            Centralized insights for streamlined management.
          </p>
        </div>
        <div className="min-w-[260px]">
          <Search
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onClear={() => setSearchTerm("")}
            placeholder="Search by keywords"
          />
        </div>
      </div>

      <div className="mt-6">
        <button
          type="button"
          onClick={() => setShowAddForm(true)}
          className="inline-flex items-center gap-2 bg-[#1D58E2] text-white text-[16px] font-semibold px-4 py-2 shadow hover:brightness-110 rounded-sm hover:cursor-pointer"
        >
          <span className="text-[18px] leading-none">+</span> ADD NEW MSP
        </button>
      </div>

      <div className="mt-6 overflow-hidden rounded-md border border-[#EFEFEF]">
        <div className="grid grid-cols-[1.2fr_0.7fr_0.8fr_0.7fr] bg-[#F3F3F3] px-6 py-3 text-[12px] font-bold tracking-wide text-[#383838]">
          <div>NAME AND EMAIL ADDRESS</div>
          <div>CREATED ON</div>
          <div>CONTACT NUMBER</div>
          <div className="flex justify-start pr-1">ACTION</div>
        </div>

        {loading ? (
          <TableSkeleton rowCount={5} />
        ) : msps.length === 0 ? (
          <div className="flex justify-center items-center py-12">
            <Empty description="No MSP's found" />
          </div>
        ) : (
          <>
            <ul className="divide-y divide-[#EFEFEF]">
              {msps.map((msp) => (
                <li
                  key={msp.id}
                  className="grid grid-cols-1 min-[900px]:grid-cols-[1.2fr_0.7fr_0.8fr_0.7fr] px-6 py-4 hover:bg-[#FAFAFA]"
                >
                  {/* col: name + email */}
                  <div className="pr-3">
                    <div className="flex items-start">
                      <button
                        type="button"
                        className="font-semibold text-[#161616] hover:text-[#1F5EFF] text-left hover:cursor-pointer"
                        onClick={() => handleMspClick(msp.id)}
                      >
                        {msp.msp_name}
                      </button>
                    </div>
                    <span className="text-[#25567C] text-[13px] ml-0 cursor-default select-text">
                      {msp.corporate_email}
                    </span>
                  </div>

                  {/* col: created on */}
                  <div className="text-[14px] text-[#3D3D3D] flex items-center">
                    {formatDate(msp.created_at)}
                  </div>

                  {/* col: phone */}
                  <div className="text-[14px] text-[#161616] flex items-center">
                    {msp.mobile_phone}
                  </div>

                  {/* col: actions */}
                  <div className="flex items-center justify-start gap-5 whitespace-nowrap flex-nowrap">
                    <button
                      type="button"
                      title="View Customers"
                      className="inline-flex items-center gap-2 text-[#1F5EFF] hover:underline whitespace-nowrap hover:cursor-pointer"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleViewCustomers(msp.id);
                      }}
                    >
                      <img
                        src={CustomerLink}
                        alt="Customers"
                        className="w-3 h-3"
                      />
                      <span className="text-[13px]">View Customers</span>
                    </button>

                    <button
                      type="button"
                      title="Delete MSP"
                      className="inline-flex items-center gap-2 text-[#E54848] hover:underline whitespace-nowrap hover:cursor-pointer"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteClick(msp);
                      }}
                    >
                      <img src={Delete} alt="Delete" className="w-4 h-4" />
                      <span className="text-[13px]">Delete MSP</span>
                    </button>
                  </div>
                </li>
              ))}
            </ul>

            {/* pagination */}
            {pagination.totalPages > 1 && (
              <div className="flex justify-end pt-6 pb-4 px-2">
                <nav>
                  <ul className="flex space-x-1 rounded-2xl px-2 py-1 bg-[#f5f5f6] text-sm font-medium items-center">
                    <li>
                      <button
                        type="button"
                        className={`px-2 py-1 rounded-full ${
                          pagination.currentPage === 1
                            ? "text-gray-400 cursor-not-allowed"
                            : ""
                        }`}
                        disabled={pagination.currentPage === 1}
                        onClick={() =>
                          setPagination((prev) => ({
                            ...prev,
                            currentPage: prev.currentPage - 1,
                          }))
                        }
                      >
                        Prev
                      </button>
                    </li>

                    {getVisiblePages(
                      pagination.currentPage,
                      pagination.totalPages
                    ).map((pg, idx) =>
                      pg === "..." ? (
                        <li
                          key={pg + idx}
                          className="px-2 py-1 select-none text-gray-400"
                        >
                          ...
                        </li>
                      ) : (
                        <li key={pg}>
                          <button
                            type="button"
                            className={`px-2 py-1 rounded-md ${
                              pagination.currentPage === pg
                                ? "bg-[#204296] text-white"
                                : "text-gray-800"
                            }`}
                            onClick={() =>
                              setPagination((prev) => ({
                                ...prev,
                                currentPage: Number(pg),
                              }))
                            }
                          >
                            {pg}
                          </button>
                        </li>
                      )
                    )}

                    <li>
                      <button
                        type="button"
                        className={`px-2 py-1 rounded-full ${
                          pagination.currentPage === pagination.totalPages
                            ? "text-gray-400 cursor-not-allowed"
                            : ""
                        }`}
                        disabled={
                          pagination.currentPage === pagination.totalPages
                        }
                        onClick={() =>
                          setPagination((prev) => ({
                            ...prev,
                            currentPage: prev.currentPage + 1,
                          }))
                        }
                      >
                        Next
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
